import { getSecretToken, getWebappRevision } from '@mode/shared/util-js';
import * as ng from 'angular';

import { queryExecutionResultComponent } from '~/components/query-execution-result/query-execution-result';
import { runHistoryRowComponent } from '~/components/run-history/run-history-row-component';

import * as config from '../../common/module-config';
import { BannerBlockComponent } from '../../components/banner-block/banner-block.component';
import { FiltersInfoComponent } from '../../components/filters-info/filters-info.component';
import { ParametersInfoFieldComponent } from '../../components/parameters-info/parameters-info-field.component';
import { ParametersInfoComponent } from '../../components/parameters-info/parameters-info.component';
import { runHistoryComponent } from '../../components/run-history/run-history-component';
import { ApiModule } from '@mode/legacy/data-access-model-js';
import { NotebookModule } from '../notebook/notebook.module';
import { ReportUiModule } from '../report-shared/ui/report-ui.module';
import { ModelModule } from '@mode/legacy/data-access-model-js';
import * as fromComponents from './components';
import { EmbedControllerFn } from './legacy-controllers/embed-controller';
import { modeCommunityShare } from './legacy-directives/community-share-dialog';
import { reportViewerRouteConfig } from './routes';
import * as fromServices from './services';
import { VizExplorerComponent } from './components/viz-explorer/viz-explorer.component';
import { DatasetLinkModule } from '~/modules/report-shared/ui/dataset-link/dataset-link.module';
import { ChartModule } from '../chart';
import { DeprecatedDisplayTableModule } from '../deprecated-display-table/deprecated-display-table.module';
import { ComponentsModule } from '~/core-app/components';
import { SharedServicesModule } from '../mode-shared-services';
import { AppServicesModule } from '../mode-app-services';
import { DowngradedReportViewerUIModule } from '../downgraded-angular/report-viewer/ui.module';
import { DowngradedSharedUIModule } from '../downgraded-angular/shared/ui.module';
import { DirectivesModule } from '~/directives';
import { downgradeInjectable } from '@angular/upgrade/static';
import { VirtualScrollFacade } from '@mode/reports/domain';
import { ReportViewerObservabilityService } from '@mode/report-viewer/domain';
import { PdfExportFacade } from '@mode/report-viewer/pdf-export/domain';
import { ModeCommonModule } from '../mode.common/common';
import { DowngradedSharedSchedulesUIModule } from '../downgraded-angular/shared/schedules-ui.module';
import { DowngradedSharedSchedulesDomainModule } from '../downgraded-angular/shared/schedules-domain.module';
import { DowngradedSharedFeatureScheduleListModule } from '../downgraded-angular/shared/feature-schedule-list.module';
import { DowngradedSharedFeatureScheduleEditorModule } from '../downgraded-angular/shared/feature-schedule-editor.module';

const appDependencies = [
  ModeCommonModule,
  ApiModule,
  AppServicesModule,
  SharedServicesModule,
  'ui.bootstrap.tooltip',
  'ui.bootstrap.dropdown',
  'ui.bootstrap.datepicker',
  'ui.bootstrap.tpls',
  'ui.bootstrap.tabs',
  'ui.router',
  ReportUiModule,
  ChartModule,
  DeprecatedDisplayTableModule,
  DirectivesModule,
  ModelModule,
  ComponentsModule,
  NotebookModule,
  'ngAria',
  'ngSanitize',
  DatasetLinkModule,
  DowngradedReportViewerUIModule,
  DowngradedSharedUIModule,
  DowngradedSharedSchedulesUIModule,
  DowngradedSharedSchedulesDomainModule,
  DowngradedSharedFeatureScheduleListModule,
  DowngradedSharedFeatureScheduleEditorModule,
];

/* prettier-ignore */
export const reportViewerApp = ng
  .module('mode.report', appDependencies)
  .config(reportViewerRouteConfig)
  .config([
    '$httpProvider',
    function ($httpProvider) {
      const version = getWebappRevision(window.document);
      const secretToken = getSecretToken(window.document);

      $httpProvider.defaults.headers.common['Accept'] = 'application/vnd.mode+json';
      $httpProvider.defaults.headers.common['Webapp-Revision'] = version;

      const variables = window.location.search.substring(1).split('&');
      let secret_key;
      for (let i = 0; i < variables.length; i++) {
        const pair = variables[i].split('=');
        if (pair[0] === 'secret_key') {
          secret_key = pair[1];
        }
      }
      const url_params = secret_key ? 'secret_key=' + secret_key + '&trk_source=report' : 'trk_source=report';

      $httpProvider.interceptors.push([
        '$q',
        function ($q) {
          return {
            request: function (config) {
              if (config.url.indexOf(document.domain) < 0 && config.url.indexOf('/api/') !== 0) {
                return config; // only track mode requests
              }
              switch (config.method) {
                case 'GET':
                  if (ng.isString(config.url) && config.url.indexOf('.html') < 0) {
                    config.url += config.url.indexOf('?') > 0 ? '&' + url_params : '?' + url_params;
                  }
                  if (secretToken && ng.isString(config.url)) {
                    config.url +=
                      config.url.indexOf('?') > 0 ? '&secret_key=' + secretToken : '?secret_key=' + secretToken;
                  }
                  break;
                case 'POST':
                  if (ng.isDefined(config.data)) {
                    config.data.trk_source = 'report';
                  } else {
                    config.data = { trk_source: 'report' };
                  }
                  if (secretToken) {
                    config.data.embed_secret = secretToken;
                  }
                  if (secret_key) {
                    config.data.secret_key = secret_key;
                  }
                  break;
              }
              return config;
            },
          };
        },
      ]);
    },
  ])
  .run([
    '$rootScope',
    '$location',
    '$transitions',
    function ($rootScope, $location, $transitions) {
      // Hack to keep the existing functionality...
      let locationSearch = {};
      $transitions.onStart({}, function (trans) {
        locationSearch = $location.search();
      });

      $transitions.onSuccess({}, function (trans) {
        //restore all query string parameters back to $location.search
        $location.search(locationSearch);
      });

      $transitions.onError({}, function (trans) {
        console.error('$stateChangeError', trans.error());
      });
    },
  ])
  .component('reportQueryExecutionResult', queryExecutionResultComponent)
  .service(fromServices.REPORT_VIEW_DATA_SERVICE_ID, fromServices.ReportViewDataService)
  .controller('EmbedController', EmbedControllerFn)
  .service(fromServices.EXPLORATION_VIEW_DATA_SERVICE_ID, fromServices.ExplorationViewDataService)
  .service('VirtualScrollFacade', downgradeInjectable(VirtualScrollFacade))
  .service('ReportViewerObservabilityService', downgradeInjectable(ReportViewerObservabilityService))
  .service('PdfExportFacade', downgradeInjectable(PdfExportFacade))
  .directive('modeCommunityShare', modeCommunityShare)
  .component('runHistory', runHistoryComponent)
  .component('runHistoryRow', runHistoryRowComponent)
  .component('bannerBlock', BannerBlockComponent)
  .component('parametersInfo', ParametersInfoComponent)
  .component('parametersInfoField', ParametersInfoFieldComponent)
  .component('filtersInfo', FiltersInfoComponent)
  .component('vizExplorer', VizExplorerComponent)
  .component(fromComponents.dashboardComponent.ID, fromComponents.dashboardComponent.reportDashboard)
  .component(fromComponents.reportViewContainer.ID, fromComponents.reportViewContainer.reportViewContainerComponent)
  .component(fromComponents.explorationContainer.ID, fromComponents.explorationContainer.explorationContainerComponent)
  .component(fromComponents.reportDetailsComponent.ID, fromComponents.reportDetailsComponent.reportDetails)
  .component(fromComponents.reportDetailsPresenter.ID, fromComponents.reportDetailsPresenter.reportDetailsPresenter)
  .component(fromComponents.reportBrowserComponent.ID, fromComponents.reportBrowserComponent.reportBrowser)
  .component(
    fromComponents.reportPresentationComponent.ID,
    fromComponents.reportPresentationComponent.reportPresentation
  )
  .component(fromComponents.reportNotebookComponent.ID, fromComponents.reportNotebookComponent.reportNotebook)
  .component(fromComponents.reportQueryResultComponent.ID, fromComponents.reportQueryResultComponent.reportQueryResult)
  .component(
    fromComponents.reportDetailsQueryComponent.ID,
    fromComponents.reportDetailsQueryComponent.reportDetailsQuery
  )
  .component(
    fromComponents.reportDetailsDatasetComponent.ID,
    fromComponents.reportDetailsDatasetComponent.reportDetailsDataset
  )
  .component(
    fromComponents.reportDetailsNotebookComponent.ID,
    fromComponents.reportDetailsNotebookComponent.reportDetailsNotebook
  )
  .component(
    fromComponents.reportDetailsCreatorComponent.ID,
    fromComponents.reportDetailsCreatorComponent.reportDetailsCreatorComponent
  )
  .component(
    fromComponents.reportDetailsTitleComponent.ID,
    fromComponents.reportDetailsTitleComponent.reportDetailsTitle
  )
  .component(
    fromComponents.reportDetailsScheduleComponent.ID,
    fromComponents.reportDetailsScheduleComponent.reportDetailsSchedule
  )
  .component(
    fromComponents.reportDetailsRunHistoryComponent.ID,
    fromComponents.reportDetailsRunHistoryComponent.reportDetailsRunHistory
  )
  .component(fromComponents.reportParamsContainer.ID, fromComponents.reportParamsContainer.reportParamsContainer)
  .component(fromComponents.emptyReportStateComponent.ID, fromComponents.emptyReportStateComponent.emptyReportState)
  .component(fromComponents.copyDatasetButtonComponent.ID, fromComponents.copyDatasetButtonComponent.copyDatasetButton)
  .component(
    fromComponents.reportVisualizationsExploreComponent.ID,
    fromComponents.reportVisualizationsExploreComponent.reportVisualizationsExplore
  )
  .component(
    fromComponents.reportLegacyVisualizationsExploreComponent.ID,
    fromComponents.reportLegacyVisualizationsExploreComponent.reportLegacyVisualizationsExplore
  )
  .component(
    fromComponents.reportViewExploreHeaderComponent.ID,
    fromComponents.reportViewExploreHeaderComponent.reportViewExploreHeader
  )
  .component(
    fromComponents.reportQueryDatasetContainer.ID,
    fromComponents.reportQueryDatasetContainer.reportQueryDatasetContainer
  )
  .component(fromComponents.vizViewerComponent.ID, fromComponents.vizViewerComponent.VizViewerComponent)
  .component(
    fromComponents.viewerEmptyChartStateComponent.ID,
    fromComponents.viewerEmptyChartStateComponent.ViewerEmptyChartStateComponent
  )
  .component(
    fromComponents.explorationDesignerComponent.ID,
    fromComponents.explorationDesignerComponent.ExplorationDesignerComponent
  )
  .component(
    fromComponents.explorationExplorerComponent.ID,
    fromComponents.explorationExplorerComponent.ExplorationExplorerComponent
  )
  .component(
    fromComponents.explorationViewerComponent.ID,
    fromComponents.explorationViewerComponent.ExplorationViewerComponent
  )
  .directive('kebabMenu', fromComponents.KebabMenuDirective);

config.setupAppModule(reportViewerApp);
